import { useEffect, useRef, useState } from "react";
import { parseHostControl } from "@/lib/host-control-message";
import { isTrustedMessageSource } from "@/lib/message-source";
import type { PreviewTabSignal, WidgetEnv } from "@/lib/use-widget-env";

/**
 * Script-tag / iframe embed: the loader forwards `Wexio('open' | 'close' | 'navigate')`
 * as `wexio:host-control:v1` messages. `open` / `close` call the shell's handlers;
 * `navigate` is returned as a signal, fed to the same path as `env.previewTab`.
 * In-DOM embeds get these as props / attributes instead, so this is inert there.
 */
export function useIframeHostControl(
  kind: WidgetEnv["kind"],
  onOpen: () => void,
  onClose: () => void,
): PreviewTabSignal | undefined {
  const [signal, setSignal] = useState<PreviewTabSignal | undefined>();
  const openRef = useRef(onOpen);
  const closeRef = useRef(onClose);
  openRef.current = onOpen;
  closeRef.current = onClose;

  useEffect(() => {
    if (kind !== "iframe") return;
    const onMessage = (event: MessageEvent) => {
      if (!isTrustedMessageSource(event, kind)) return;
      const control = parseHostControl(event.data);
      if (!control) return;
      if (control.command === "open") openRef.current();
      else if (control.command === "close") closeRef.current();
      else setSignal(control.signal);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [kind]);

  return signal;
}
